import { datadogRum } from '@datadog/browser-rum'
import { ClientError } from '@owner/olympus-client'
import { AxiosError } from 'axios'
import { storeToRefs } from 'pinia'
import { type RouteLocationRaw, useRouter } from 'vue-router'

import { useIntercomStore } from '@/core/public/useIntercomStore'
import { logError } from '@/logger'
import { analytics } from '@/plugins/analytics'
import { cobrowse } from '@/plugins/cobrowse'
import { FeatureFlags } from '@/plugins/featureFlags'
import { useFlag } from '@/utils/featureFlagStore'

import { clearPendingRedirect, popPendingRedirect } from './pendingRedirect'
import {
  type AuthLogoutOptions,
  type AuthLogoutReason,
  type MultiFactorAuthVerifyParams,
  type MultiFactorVerifyResponse,
} from './types'
import { useAuthStore } from './useAuthStore'

// Deliberate: the user chose to end the session, or it timed out on its own
// schedule. Every other reason is an error-recovery path (401, expired
// authorizer token, CSRF, etc.) where a hard reload risks looping against
// auth-service instead of just re-rendering the login route.
const DELIBERATE_LOGOUT_REASONS: ReadonlySet<AuthLogoutReason> =
  new Set<AuthLogoutReason>(['user-initiated', 'session-timer-expired'])

interface UseAuthActions {
  login: (args: { email: string; password: string }) => Promise<void>
  resetPassword: (args: { password: string; token: string }) => Promise<void>
  completeCredentialSetup: (args: {
    setupToken: string
    email: string
    password: string
  }) => Promise<void>
  multiFactorLogin: (
    args: MultiFactorAuthVerifyParams
  ) => Promise<MultiFactorVerifyResponse>
  logout: (options?: AuthLogoutOptions) => Promise<void>
  resendOtp: (args: {
    sessionId: string
    method: 'email' | 'phone'
    mfaToken?: string
  }) => Promise<{ success: true } | { success: false; error: string }>
}

export const useAuthActions = (): UseAuthActions => {
  const router = useRouter()
  const reloadOnLogout = useFlag('hermes.reloadOnLogout')

  const intercomStore = useIntercomStore()

  const authStore = useAuthStore()
  const { user, home, brandSession } = storeToRefs(authStore)

  const sanitizePath = (path: string): RouteLocationRaw => {
    const { name, params, query } = router.resolve(path)

    if (name && name !== 'not-found' && name !== '') {
      return { name, params, query }
    } else {
      return home.value
    }
  }

  const configurePlugins = async (): Promise<void> => {
    if (!user.value) throw new Error('[Auth] User Not Defined On Plugin Config')

    if (user.value.email)
      void FeatureFlags.setContextField('userId', user.value.email)
    else void FeatureFlags.setContextField('userId', user.value.id)

    const userData = {
      brandSession: brandSession.value,
      createdAt: user.value.createdAt,
      email: user.value.email,
      firstName: user.value.firstName,
      id: user.value.id,
      lastName: user.value.lastName,
      name: `${user.value.firstName} ${user.value.lastName}`,
    }

    analytics.identify(user.value.id)
    analytics.setUserData(userData)
    datadogRum.setUser({
      ...userData,
      email: user.value.email ?? undefined,
    })

    void intercomStore.initializeIntercom()

    void cobrowse.init({
      app: 'hermes',
      brand: '',
      brandId: brandSession.value?.brandId ?? '',
      email: user.value.email ?? '',
      userName: `${user.value.firstName} ${user.value.lastName}`,
    })
  }

  const logoutPlugins = (): void => {
    intercomStore.shutdownIntercom()
  }

  const redirectToUserHome = async (): Promise<void> => {
    if (!user.value)
      throw new Error('[Auth] User Not Defined On Redirect To Home')

    const pendingRedirect = popPendingRedirect()

    if (pendingRedirect) {
      await router.push(sanitizePath(pendingRedirect))
    } else {
      await router.push(home.value)
    }
  }

  const resendOtp = async ({
    sessionId,
    method,
    mfaToken,
  }: {
    sessionId: string
    method: 'email' | 'phone'
    mfaToken?: string
  }): Promise<{ success: true } | { success: false; error: string }> => {
    try {
      const response = await authStore.resendOtp({
        method,
        mfaToken,
        sessionId,
      })
      await router.push({
        name: 'auth.multiFactorAuth.v2',
        query: {
          hint: response.hint,
          method: response.method,
          mfaOptions: encodeURIComponent(JSON.stringify(response.mfaOptions)),
          mfaToken,
          sessionId: response.sessionId,
        },
      })
      return { success: true }
    } catch (error) {
      if (error instanceof AxiosError) {
        return {
          error:
            error.response?.data.message ??
            error.message ??
            'Something went wrong',
          success: false,
        }
      }
      if ('message' in error) {
        return {
          error: error.message,
          success: false,
        }
      }
      return {
        error: 'Something went wrong',
        success: false,
      }
    }
  }

  const login = async ({
    email,
    password,
  }: {
    email: string
    password: string
  }): Promise<void> => {
    const result = await authStore.login({ email, password })

    if (result.status === 'requires-2fa') {
      analytics.track('Multi Factor Auth Challenge Started')

      await router.push({
        name: 'auth.multiFactorAuth.v2',
        query: {
          email,
          ffPlainOtpEnabled: 'true',
          hint: result.hint,
          method: result.method,
          mfaOptions: encodeURIComponent(JSON.stringify(result.mfaOptions)),
          mfaToken: result.mfaToken,
          sessionId: result.sessionId,
        },
      })

      return
    }

    await configurePlugins()
    analytics.track('Login', { via: 'login' })

    await redirectToUserHome()
  }

  const resetPassword = async ({
    password,
    token,
  }: {
    password: string
    token: string
  }): Promise<void> => {
    await authStore.resetPassword({
      password,
      token,
    })

    await configurePlugins()
    analytics.track('Login', { via: 'reset-password' })

    await redirectToUserHome()
  }

  const completeCredentialSetup = async ({
    setupToken,
    email,
    password,
  }: {
    setupToken: string
    email: string
    password: string
  }): Promise<void> => {
    try {
      await authStore.completeCredentialSetup({ email, password, setupToken })
    } catch (error) {
      logError(error, { action: 'completeCredentialSetup' })
      throw error
    }

    await router.push({ name: 'auth.login' })
  }

  const multiFactorLogin = async (params: {
    sessionId: string
    code: string
    mfaToken?: string
  }): Promise<MultiFactorVerifyResponse> => {
    try {
      await authStore.multiFactorLogin(params)

      await configurePlugins()
      analytics.track('Login', { via: 'mfa-login' })

      await redirectToUserHome()

      return { error: null, success: true }
    } catch (error) {
      if (error instanceof AxiosError) {
        return {
          error: error.response?.data.message ?? error.message,
          success: false,
        }
      }
      if (error instanceof ClientError) {
        return {
          error: error.message,
          success: false,
        }
      }

      throw error
    }
  }

  const logout = async (options?: AuthLogoutOptions): Promise<void> => {
    const endedLiveSession = await authStore.logout(options)
    clearPendingRedirect()

    // The teardown flips `isAuthenticated`, which re-enters here through
    // useSessionManager's watcher, and an in-flight 401 can converge on the
    // same logout via the `auth.logout` emitter. Only the call that ended the
    // session reports it, or one user action emits two or three `Logout`s.
    if (endedLiveSession) analytics.track('Logout')

    logoutPlugins()

    // don't redirect to login if already on a login route
    if (
      router.currentRoute.value.fullPath !== '/' &&
      router.currentRoute.value.fullPath !== '/login'
    ) {
      if (
        reloadOnLogout.value &&
        options?.reason &&
        DELIBERATE_LOGOUT_REASONS.has(options.reason)
      ) {
        window.location.assign(router.resolve({ name: 'auth.login' }).href)
      } else {
        await router.push({
          name: 'auth.login',
        })
      }
    }
  }

  return {
    completeCredentialSetup,
    login,
    logout,
    multiFactorLogin,
    resendOtp,
    resetPassword,
  }
}
